// src/pages/JoinUs.jsx
import { useEffect, useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { BACKGROUNDS } from '../utils/bgStyle';
import { submitMembership } from '../services/api';

export default function JoinUs({ onDonate }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', type: 'individual', motivation: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await submitMembership(form);
      setSuccess(true);
      setForm({ full_name: '', email: '', phone: '', type: 'individual', motivation: '' });
    } catch (err) {
      setError(err.message || (lang === 'fr' ? 'Erreur lors de l\'envoi.' : 'Submission failed.'));
    } finally {
      setLoading(false);
    }
  };

  const opportunities = [
    { key: 'membership', icon: 'fa-id-card', anchor: '#membership' },
    { key: 'internship', icon: 'fa-briefcase', anchor: '#internship' },
    { key: 'volunteer', icon: 'fa-hands-helping', anchor: '#volunteer' },
  ];

  return (
    <>
      {/* ── Hero ── */}
      <section id="home" className="hero" style={BACKGROUNDS.hero}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: 800, margin: '0 auto' }}>
            <div className="fade-in-up">
              <span className="label" style={{ display: 'inline-block', padding: '8px 20px', background: 'var(--bg-card)', color: 'var(--secondary)', fontWeight: 700, fontSize: '0.875rem', borderRadius: 20, marginBottom: '1.5rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                WACEAS
              </span>
              <h1 style={{ fontSize: '3.5rem', marginBottom: '1.5rem', lineHeight: 1.2 }}>
                <span className="highlight">{t('joinus.hero.title')}</span>
              </h1>
              <p style={{ fontSize: '1.25rem', color: 'rgba(26,26,26,0.7)', lineHeight: 1.8, marginBottom: '2.5rem' }}>
                {t('joinus.hero.subtitle')}
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a href="#membership" className="btn-primary" onClick={e => { e.preventDefault(); document.querySelector('#membership')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  <i className="fas fa-user-plus"></i> {t('joinus.opportunities.membership.title')}
                </a>
                <button className="btn-outline" onClick={onDonate}>
                  <i className="fas fa-heart"></i> {t('donate.nav')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Overview ── */}
      <section className="bg-muted">
        <div className="container">
          <div className="section-header fade-in-up">
            <span className="label">{t('joinus.overview.label')}</span>
            <h2><span className="highlight">{t('joinus.overview.title')}</span></h2>
            <p>{t('joinus.overview.description')}</p>
          </div>
          <div className="grid grid-3">
            {opportunities.map(({ key, icon, anchor }, i) => {
              const benefits = t(`joinus.opportunities.${key}.benefits`) || [];
              return (
                <div key={key} className="opportunity-card fade-in-up" style={{ animationDelay: `${0.1 * (i + 1)}s` }}>
                  <div className="card-icon" style={{ marginBottom: '1.5rem' }}><i className={`fas ${icon}`}></i></div>
                  <h3 style={{ marginBottom: '1rem' }}>{t(`joinus.opportunities.${key}.title`)}</h3>
                  <p style={{ color: 'rgba(26,26,26,0.7)', marginBottom: '1rem' }}>{t(`joinus.opportunities.${key}.description`)}</p>
                  <ul>
                    {Array.isArray(benefits) && benefits.map((b, j) => (
                      <li key={j}><i className="fas fa-check-circle"></i> {b}</li>
                    ))}
                  </ul>
                  <a href={anchor} className="btn-primary" style={{ marginTop: '1.5rem', justifyContent: 'center' }}
                    onClick={e => { e.preventDefault(); document.querySelector(anchor)?.scrollIntoView({ behavior: 'smooth' }); }}>
                    {t('joinus.opportunities.membership.title') && 'En savoir plus'}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Membership ── */}
      <section id="membership" className="bg-card">
        <div className="container">
          <div className="section-header fade-in-up">
            <span className="label">{t('joinus.overview.label')}</span>
            <h2><span className="highlight">{t('joinus.membership.title')}</span></h2>
          </div>
          <div className="grid grid-2" style={{ marginBottom: '3rem' }}>
            {['individual', 'organization'].map((type, i) => (
              <div key={type} className="membership-type-card fade-in-up" style={{ animationDelay: `${0.1 * (i + 1)}s` }}>
                <div className="card-icon" style={{ marginBottom: '1.5rem' }}>
                  <i className={`fas ${type === 'individual' ? 'fa-user' : 'fa-building'}`}></i>
                </div>
                <h3 style={{ marginBottom: '1rem' }}>{t(`joinus.membership.types.${type}.title`)}</h3>
                <p style={{ color: 'rgba(26,26,26,0.7)' }}>{t(`joinus.membership.types.${type}.description`)}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-2">
            <div className="card fade-in-up">
              <h3 style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: 12 }}>
                <i className="fas fa-star" style={{ color: 'var(--primary)' }}></i>
                {t('joinus.membership.benefits.title')}
              </h3>
              <ul className="requirement-list">
                {(t('joinus.membership.benefits.list') || []).map((b, i) => (
                  <li key={i}><i className="fas fa-check-circle"></i> {b}</li>
                ))}
              </ul>
            </div>
            <div className="card fade-in-up" style={{ animationDelay: '0.1s' }}>
              <h3 style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: 12 }}>
                <i className="fas fa-clipboard-list" style={{ color: 'var(--primary)' }}></i>
                {t('joinus.membership.requirements.title')}
              </h3>
              <ul className="requirement-list">
                {(t('joinus.membership.requirements.list') || []).map((r, i) => (
                  <li key={i}><i className="fas fa-arrow-right"></i> {r}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* ── Application Form ── */}
          <div className="card fade-in-up" style={{ marginTop: '3rem', maxWidth: 640, margin: '3rem auto 0' }}>
            <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 12 }}>
              <i className="fas fa-user-plus" style={{ color: 'var(--primary)' }}></i>
              {lang === 'fr' ? 'Formulaire de candidature' : 'Application Form'}
            </h3>
            {success ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--primary)' }}>
                <i className="fas fa-check-circle" style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem' }}></i>
                <h4>{lang === 'fr' ? 'Candidature envoyée !' : 'Application submitted!'}</h4>
                <p style={{ color: 'rgba(26,26,26,0.7)', marginTop: '0.5rem' }}>
                  {lang === 'fr' ? 'Nous examinerons votre demande et vous contacterons bientôt.' : 'We will review your application and contact you soon.'}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '1.25rem' }}>
                  <input className="form-control" type="text" placeholder={lang === 'fr' ? 'Nom complet *' : 'Full name *'}
                    value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} required />
                </div>
                <div style={{ marginBottom: '1.25rem' }}>
                  <input className="form-control" type="email" placeholder={lang === 'fr' ? 'Adresse email *' : 'Email address *'}
                    value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
                </div>
                <div style={{ marginBottom: '1.25rem' }}>
                  <input className="form-control" type="tel" placeholder={lang === 'fr' ? 'Téléphone (optionnel)' : 'Phone (optional)'}
                    value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
                <div style={{ marginBottom: '1.25rem' }}>
                  <select className="form-control" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={{ appearance: 'auto' }}>
                    <option value="individual">{lang === 'fr' ? 'Individuel' : 'Individual'}</option>
                    <option value="organization">{lang === 'fr' ? 'Organisation' : 'Organization'}</option>
                    <option value="intern">{lang === 'fr' ? 'Stagiaire' : 'Intern'}</option>
                    <option value="volunteer">{lang === 'fr' ? 'Bénévole' : 'Volunteer'}</option>
                  </select>
                </div>
                <div style={{ marginBottom: '1.25rem' }}>
                  <textarea className="form-control" placeholder={lang === 'fr' ? 'Motivation (optionnel)' : 'Motivation (optional)'}
                    value={form.motivation} onChange={e => setForm(f => ({ ...f, motivation: e.target.value }))} rows={4} />
                </div>
                {error && <p style={{ color: '#e53e3e', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</p>}
                <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
                  <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                  <span>{loading ? (lang === 'fr' ? 'Envoi...' : 'Sending...') : (lang === 'fr' ? 'Soumettre ma candidature' : 'Submit my application')}</span>
                </button>
              </form>
            )}
          </div>
          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <p style={{ color: 'rgba(26,26,26,0.5)', fontSize: '0.9rem' }}>
              {lang === 'fr' ? 'Vous pouvez aussi nous contacter sur ' : 'You can also reach us on '}
              <a href="https://wa.me/237699065019" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>
                <i className="fab fa-whatsapp"></i> WhatsApp
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* ── Internship ── */}
      <section id="internship" className="bg-muted">
        <div className="container">
          <div className="section-header fade-in-up">
            <span className="label">WACEAS</span>
            <h2><span className="highlight">{t('joinus.opportunities.internship.title')}</span></h2>
          </div>
          <div className="grid grid-2">
            <div className="card fade-in-up">
              <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 12 }}>
                <i className="fas fa-clock" style={{ color: 'var(--primary)' }}></i>
                Durée & Engagement
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[{ title: 'Stage Court Terme', desc: '3-6 mois, minimum 20h/semaine' }, { title: 'Stage Long Terme', desc: '6-12 mois, minimum 30h/semaine' }].map((d, i) => (
                  <div key={i} style={{ padding: '1rem', background: 'var(--bg-card)', borderRadius: 'var(--border-radius)', borderLeft: '4px solid var(--primary)' }}>
                    <strong>{d.title}</strong>
                    <p style={{ color: 'rgba(26,26,26,0.7)', marginTop: '0.25rem' }}>{d.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="card fade-in-up" style={{ animationDelay: '0.1s' }}>
              <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 12 }}>
                <i className="fas fa-gift" style={{ color: 'var(--primary)' }}></i>
                Ce Que Vous Gagnerez
              </h3>
              <ul className="requirement-list">
                {(t('joinus.opportunities.internship.benefits') || []).map((b, i) => (
                  <li key={i}><i className="fas fa-check-circle"></i> {b}</li>
                ))}
              </ul>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <a href="https://wa.me/237699065019" target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ fontSize: '1.125rem', padding: '16px 36px' }}>
              <i className="fab fa-whatsapp"></i> Postuler via WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* ── Volunteer ── */}
      <section id="volunteer" className="bg-card">
        <div className="container">
          <div className="section-header fade-in-up">
            <span className="label">WACEAS</span>
            <h2><span className="highlight">{t('joinus.opportunities.volunteer.title')}</span></h2>
            <p>{t('joinus.opportunities.volunteer.description')}</p>
          </div>
          <div className="grid grid-2">
            <div className="feature-card fade-in-up">
              <h3><i className="fas fa-heart"></i> Opportunités de Bénévolat</h3>
              <ul className="requirement-list" style={{ marginTop: '1rem' }}>
                {['Sensibilisation communautaire', 'Ateliers éducatifs', "Organisation d'événements", 'Collecte de fonds', 'Traduction', 'Support administratif'].map((o, i) => (
                  <li key={i}><i className="fas fa-check-circle"></i> {o}</li>
                ))}
              </ul>
            </div>
            <div className="feature-card fade-in-up" style={{ animationDelay: '0.1s' }}>
              <h3><i className="fas fa-star"></i> Avantages Bénévoles</h3>
              <ul className="requirement-list" style={{ marginTop: '1rem' }}>
                {(t('joinus.opportunities.volunteer.benefits') || []).map((b, i) => (
                  <li key={i}><i className="fas fa-check-circle"></i> {b}</li>
                ))}
              </ul>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <a href="https://wa.me/237699065019" target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ fontSize: '1.125rem', padding: '16px 36px' }}>
              <i className="fab fa-whatsapp"></i> Devenir Bénévole
            </a>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section style={BACKGROUNDS.cta}>
        <div className="container">
          <div className="cta-section fade-in-up">
            <h2>{t('cta.title')}</h2>
            <p>{t('cta.description')}</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="https://wa.me/237699065019" target="_blank" rel="noopener noreferrer" className="btn-white">
                <i className="fas fa-user"></i> {t('cta.buttons.individual')}
              </a>
              <button className="btn-white" onClick={onDonate}>
                <i className="fas fa-heart"></i> {t('donate.nav')}
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
